import * as preact from "preact";

import * as modalController from "modalController";
import readlangClient from "readlangClient";
import validateEmail from "shared/validateEmail";
import Dialog from "views/Dialog";

type Props = {
  userID: string;
  currentEmail: string;
  onEmailChanged: () => void;
};

type State = {
  newEmail: string;
  submitting: boolean;
  error: string | null;
  success: { oldEmail: string; newEmail: string } | null;
};

export default class ChangeEmailDialog extends preact.Component<Props, State> {
  state: State = {
    newEmail: "",
    submitting: false,
    error: null,
    success: null,
  };

  async changeEmail() {
    const { newEmail } = this.state;
    const trimmedEmail = newEmail.trim().toLowerCase();

    if (!trimmedEmail) {
      this.setState({ error: "Please enter an email address" });
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      this.setState({ error: "Invalid email format" });
      return;
    }

    if (trimmedEmail === this.props.currentEmail.toLowerCase()) {
      this.setState({ error: "New email is the same as the current email" });
      return;
    }

    this.setState({ submitting: true, error: null });

    try {
      const response = await readlangClient.request(
        "POST",
        `/api/admin/user/${this.props.userID}/changeEmail`,
        { newEmail: trimmedEmail }
      );

      this.setState({
        success: response.data,
        submitting: false,
      });
      this.props.onEmailChanged();
    } catch (error: any) {
      console.error("Change email error:", error);
      const errorMessage = error.jqXHR?.alreadyNotified
        ? "Connection problem. Please try again."
        : error.jqXHR?.data?.error ||
          error.jqXHR?.responseJSON?.error ||
          error.data?.error ||
          error.error ||
          "Failed to change email";
      this.setState({ error: errorMessage, submitting: false });
    }
  }

  render() {
    const { currentEmail } = this.props;
    const { newEmail, submitting, error, success } = this.state;

    if (success) {
      return (
        <Dialog
          class="changeEmailDialog"
          success={
            <>
              <p>
                Successfully changed email from{" "}
                <strong>{success.oldEmail}</strong> to{" "}
                <strong>{success.newEmail}</strong>
              </p>
              <button onClick={() => modalController.cancel()}>Close</button>
            </>
          }
        />
      );
    }

    return (
      <Dialog
        class="changeEmailDialog"
        heading="Change email address"
        error={error}
        actions={
          <button onClick={() => this.changeEmail()} disabled={submitting}>
            {submitting ? "Changing..." : "Change Email"}
          </button>
        }
      >
        <p>
          Current email: <strong>{currentEmail}</strong>
        </p>
        <input
          type="email"
          placeholder="Enter new email address"
          value={newEmail}
          onInput={(event) =>
            this.setState({
              newEmail: event.currentTarget.value,
              error: null,
            })
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              this.changeEmail();
            }
          }}
          disabled={submitting}
          style={{ width: "100%" }}
        />
      </Dialog>
    );
  }
}
